import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import {
  Settings,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Calendar,
  Users,
  Clock,
  Plus,
  Trash,
  Edit,
  Save,
  X,
  ChevronDown,
} from 'lucide-react'
import {
  PriceConfig,
  BusinessModel,
  OTAChannel,
  PricingRule,
  LOSCategory,
  PAXCategory,
} from '../types'

interface PriceConfigPanelProps {
  priceConfig: PriceConfig
  businessModel: BusinessModel
  otaChannels: OTAChannel[]
  pricingRules: PricingRule[]
  losCategories: LOSCategory[]
  paxCategories: PAXCategory[]
  onPriceConfigChange: (config: PriceConfig) => void
  onBusinessModelChange: (model: BusinessModel) => void
  onChannelChange: (channels: OTAChannel[]) => void
  onPricingRuleToggle: (ruleId: string) => void
  onPricingRuleChange: (rules: PricingRule[]) => void
  onLOSCategoryChange: (categories: LOSCategory[]) => void
  onPAXCategoryChange: (categories: PAXCategory[]) => void
  onClearOverrides?: () => void
  overrideCount: number
  suggestedMaxPrice?: number
}

const PriceConfigPanel: React.FC<PriceConfigPanelProps> = ({
  priceConfig,
  businessModel,
  otaChannels,
  pricingRules,
  losCategories,
  paxCategories,
  onPriceConfigChange,
  onBusinessModelChange,
  onChannelChange,
  onPricingRuleToggle,
  onPricingRuleChange,
  onLOSCategoryChange,
  onPAXCategoryChange,
  onClearOverrides,
  overrideCount,
  suggestedMaxPrice,
}) => {
  const [activeTab, setActiveTab] = useState<'business' | 'rules' | 'channels'>(
    'business',
  )
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null)
  const [editingLOSId, setEditingLOSId] = useState<string | null>(null)
  const [editingPAXId, setEditingPAXId] = useState<string | null>(null)
  const [newRule, setNewRule] = useState<PricingRule | null>(null)
  const [newLOS, setNewLOS] = useState<LOSCategory | null>(null)
  const [newPAX, setNewPAX] = useState<PAXCategory | null>(null)
  const [showTypeOptions, setShowTypeOptions] = useState(false)
  const [showWorkModelOptions, setShowWorkModelOptions] = useState(false)
  const [showPriceModelOptions, setShowPriceModelOptions] = useState(false)

  // Form state
  const [editedRule, setEditedRule] = useState<PricingRule | null>(null)
  const [editedLOS, setEditedLOS] = useState<LOSCategory | null>(null)
  const [editedPAX, setEditedPAX] = useState<PAXCategory | null>(null)

  // Update form state when editing items change
  useEffect(() => {
    if (editingRuleId) {
      const rule = pricingRules.find((r) => r.id === editingRuleId)
      if (rule) {
        setEditedRule({ ...rule })
      }
    } else {
      setEditedRule(null)
    }
  }, [editingRuleId, pricingRules])

  useEffect(() => {
    if (editingLOSId) {
      const los = losCategories.find((l) => l.id === editingLOSId)
      if (los) {
        setEditedLOS({ ...los })
      }
    } else {
      setEditedLOS(null)
    }
  }, [editingLOSId, losCategories])

  useEffect(() => {
    if (newRule) {
      setEditedRule({ ...newRule })
    }
  }, [newRule])

  useEffect(() => {
    if (newLOS) {
      setEditedLOS({ ...newLOS })
    }
  }, [newLOS])

  useEffect(() => {
    if (newPAX) {
      setEditedPAX({ ...newPAX })
    }
  }, [newPAX])

  const handlePriceChange = (field: keyof PriceConfig, value: string) => {
    const numValue = parseFloat(value) || 0
    onPriceConfigChange({
      ...priceConfig,
      [field]: numValue,
    })
  }

  const handleChannelToggle = (channelId: string) => {
    const updatedChannels = otaChannels.map((channel) =>
      channel.id === channelId
        ? { ...channel, enabled: !channel.enabled }
        : channel,
    )
    onChannelChange(updatedChannels)
  }

  const handleChannelMarginChange = (channelId: string, margin: string) => {
    const numMargin = parseFloat(margin) || 0
    const updatedChannels = otaChannels.map((channel) =>
      channel.id === channelId
        ? { ...channel, marginPercent: numMargin }
        : channel,
    )
    onChannelChange(updatedChannels)
  }

  const useSuggestedMax = () => {
    if (suggestedMaxPrice) {
      onPriceConfigChange({
        ...priceConfig,
        maxPrice: suggestedMaxPrice,
      })
    }
  }

  const handleAddRule = () => {
    setNewRule({
      id: `rule_${Date.now()}`,
      name: 'New Rule',
      description: 'Description of the new rule',
      priceModifier: 0,
      enabled: true,
    })
  }

  const handleSaveNewRule = () => {
    if (newRule) {
      onPricingRuleChange([...pricingRules, newRule])
      setNewRule(null)
    }
  }

  const handleCancelNewRule = () => {
    setNewRule(null)
  }

  const handleEditRule = (ruleId: string) => {
    setEditingRuleId(ruleId)
  }

  const handleSaveRule = () => {
    if (editedRule) {
      if (newRule) {
        onPricingRuleChange([...pricingRules, editedRule])
        setNewRule(null)
      } else {
        const updatedRules = pricingRules.map((r) =>
          r.id === editedRule.id ? editedRule : r,
        )
        onPricingRuleChange(updatedRules)
        setEditingRuleId(null)
      }
    }
  }

  const handleDeleteRule = (ruleId: string) => {
    const updatedRules = pricingRules.filter((rule) => rule.id !== ruleId)
    onPricingRuleChange(updatedRules)
  }

  const handleAddLOS = () => {
    setNewLOS({
      id: `los-${Date.now()}`,
      name: '',
      minNights: 1,
      priceModifier: 0,
      modifierType: 'percent',
    })
  }

  const handleSaveNewLOS = () => {
    if (newLOS) {
      onLOSCategoryChange([...losCategories, newLOS])
      setNewLOS(null)
    }
  }

  const handleCancelNewLOS = () => {
    setNewLOS(null)
  }

  const handleEditLOS = (losId: string) => {
    setEditingLOSId(losId)
  }

  const handleSaveLOS = () => {
    if (editedLOS) {
      if (newLOS) {
        onLOSCategoryChange([...losCategories, editedLOS])
        setNewLOS(null)
      } else {
        const updatedCategories = losCategories.map((l) =>
          l.id === editedLOS.id ? editedLOS : l,
        )
        onLOSCategoryChange(updatedCategories)
        setEditingLOSId(null)
      }
    }
  }

  const handleDeleteLOS = (losId: string) => {
    const updatedCategories = losCategories.filter((los) => los.id !== losId)
    onLOSCategoryChange(updatedCategories)
  }

  const handleAddPAX = () => {
    const newPaxCategory = {
      id: `pax_${Date.now()}`,
      name: 'New PAX Category',
      persons: 1,
      priceModifier: 0,
      isDefault: false,
    }
    setNewPAX(newPaxCategory)
    setEditedPAX(newPaxCategory)
  }

  const handleSaveNewPAX = () => {
    if (newPAX) {
      onPAXCategoryChange([...paxCategories, newPAX])
      setNewPAX(null)
    }
  }

  const handleCancelNewPAX = () => {
    setNewPAX(null)
  }

  const handleEditPAX = (paxId: string) => {
    console.log('handleEditPAX called with id:', paxId)
    const paxToEdit = paxCategories.find((p) => p.id === paxId)
    console.log('Found PAX to edit:', paxToEdit)
    if (paxToEdit) {
      setEditingPAXId(paxId)
      setEditedPAX({ ...paxToEdit })
      console.log('Set editingPAXId and editedPAX')
    }
  }

  const handleSavePAX = () => {
    if (editedPAX) {
      // Validate required fields
      if (!editedPAX.name || editedPAX.name.trim() === '') {
        alert('Please enter a name for the PAX category')
        return
      }
      if (!editedPAX.persons || editedPAX.persons < 1) {
        alert('Please enter a valid number of persons (minimum 1)')
        return
      }

      if (newPAX) {
        onPAXCategoryChange([...paxCategories, editedPAX])
        setNewPAX(null)
      } else {
        const updatedCategories = paxCategories.map((p) =>
          p.id === editedPAX.id ? editedPAX : p,
        )
        onPAXCategoryChange(updatedCategories)
        setEditingPAXId(null)
      }
    }
  }

  const handleDeletePAX = (paxId: string) => {
    const updatedCategories = paxCategories.filter((pax) => pax.id !== paxId)
    onPAXCategoryChange(updatedCategories)
  }

  const updateRule = (field: keyof PricingRule, value: any) => {
    if (editedRule) {
      setEditedRule((prev) => (prev ? { ...prev, [field]: value } : null))
    }
  }

  const updateLOS = (field: keyof LOSCategory, value: any) => {
    if (editedLOS) {
      setEditedLOS((prev) => (prev ? { ...prev, [field]: value } : null))
    }
  }

  const updatePAX = (field: keyof PAXCategory, value: any) => {
    if (editedPAX) {
      setEditedPAX((prev) => (prev ? { ...prev, [field]: value } : null))
    }
  }

  const renderRuleForm = (isNew: boolean = false) => {
    if (!editedRule) return null

    // Determine rule type for label
    let ruleTypeLabel = ''
    let ruleTypeColor = ''
    const nameLower = editedRule.name.toLowerCase()
    if (
      nameLower.includes('holiday') ||
      nameLower.includes('special') ||
      (editedRule.dateRangeFilter &&
        !editedRule.dayOfWeekFilter &&
        !editedRule.monthFilter)
    ) {
      ruleTypeLabel = 'Special Day'
      ruleTypeColor = 'bg-pink-100 text-pink-700 border-pink-300'
    } else if (
      editedRule.monthFilter ||
      (editedRule.dateRangeFilter && !ruleTypeLabel)
    ) {
      ruleTypeLabel = 'Season'
      ruleTypeColor = 'bg-blue-100 text-blue-700 border-blue-300'
    } else if (editedRule.dayOfWeekFilter) {
      ruleTypeLabel = 'Repeating Day'
      ruleTypeColor = 'bg-green-100 text-green-700 border-green-300'
    }

    return (
      <div className="border rounded-lg p-4 bg-white">
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Name</label>
            <input
              type="text"
              value={editedRule.name}
              onChange={(e) => updateRule('name', e.target.value)}
              className="pricing-input w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Description
            </label>
            <input
              type="text"
              value={editedRule.description}
              onChange={(e) => updateRule('description', e.target.value)}
              className="pricing-input w-full text-sm"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Price Modifier (%)
            </label>
            <input
              type="number"
              value={editedRule.priceModifier}
              onChange={(e) =>
                updateRule('priceModifier', parseFloat(e.target.value) || 0)
              }
              className="pricing-input w-full text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="relative inline-flex items-center">
              <button
                type="button"
                className="p-2 -m-2 focus:outline-none"
                tabIndex={0}
                aria-label={`Toggle ${editedRule.name}`}
                onClick={(e) => {
                  e.stopPropagation()
                  updateRule('enabled', !editedRule.enabled)
                }}
              >
                <input
                  type="checkbox"
                  checked={editedRule.enabled}
                  readOnly
                  className="rounded text-primary-600 focus:ring-primary-500 align-middle pointer-events-none"
                  style={{ width: 12, height: 12, minWidth: 16 }}
                />
              </button>
            </span>
          </div>

          <div className="flex justify-between pt-2">
            <div className="flex gap-2">
              <button
                onClick={
                  isNew ? handleCancelNewRule : () => setEditingRuleId(null)
                }
                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRule}
                className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
              >
                Save
              </button>
            </div>
            {!isNew && (
              <button
                onClick={() => {
                  handleDeleteRule(editedRule.id)
                  setEditingRuleId(null)
                }}
                className="px-3 py-1 text-xs bg-danger-600 text-white rounded hover:bg-danger-700 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderLOSForm = (isNew: boolean = false) => {
    if (!editedLOS) return null

    return (
      <div className="border rounded-lg p-4 bg-white">
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Name</label>
            <input
              type="text"
              value={editedLOS.name}
              onChange={(e) => updateLOS('name', e.target.value)}
              className="pricing-input w-full text-sm"
            />
          </div>

          {/* Min/Max nights */}
          <div className="flex gap-4 mb-2">
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">Min</label>
              <input
                type="number"
                value={editedLOS.minNights}
                onChange={(e) =>
                  updateLOS('minNights', parseInt(e.target.value) || 1)
                }
                className="pricing-input w-full text-sm"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">Max</label>
              <input
                type="number"
                value={editedLOS.maxNights || ''}
                onChange={(e) =>
                  updateLOS('maxNights', parseInt(e.target.value) || undefined)
                }
                className="pricing-input w-full text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={!!editedLOS.isDefault}
              onChange={(e) => updateLOS('isDefault', e.target.checked)}
              className="mr-2"
            />
            <label className="text-sm text-gray-600">Default</label>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Selling price difference type
            </label>
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50 w-full mb-2">
              <button
                type="button"
                className={`w-1/2 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  editedLOS.sellingPriceDifferenceType === 'absolute'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() =>
                  updateLOS('sellingPriceDifferenceType', 'absolute')
                }
              >
                Absolute
              </button>
              <button
                type="button"
                className={`w-1/2 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  editedLOS.sellingPriceDifferenceType === 'percent'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() =>
                  updateLOS('sellingPriceDifferenceType', 'percent')
                }
              >
                Percent
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Selling price difference
            </label>
            <div className="relative">
              <input
                type="number"
                value={editedLOS.sellingPriceDifference ?? ''}
                onChange={(e) =>
                  updateLOS(
                    'sellingPriceDifference',
                    parseFloat(e.target.value) || 0,
                  )
                }
                className="pricing-input w-full text-sm pr-8"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                {editedLOS.sellingPriceDifferenceType === 'absolute'
                  ? '€'
                  : '%'}
              </span>
            </div>
          </div>

          <div className="flex justify-between pt-2 relative z-10">
            <div className="flex gap-2">
              <button
                onClick={
                  isNew ? handleCancelNewLOS : () => setEditingLOSId(null)
                }
                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={isNew ? handleSaveNewLOS : handleSaveLOS}
                className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
              >
                Save
              </button>
            </div>
            {!isNew && (
              <button
                onClick={() => {
                  handleDeleteLOS(editedLOS.id)
                  setEditingLOSId(null)
                }}
                className="px-3 py-1 text-xs bg-danger-600 text-white rounded hover:bg-danger-700 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderPAXForm = (isNew: boolean = false) => {
    console.log('renderPAXForm called, editedPAX:', editedPAX)
    console.log('isNew:', isNew)
    console.log('editingPAXId:', editingPAXId)

    if (!editedPAX) {
      console.log('No editedPAX, returning null')
      return null
    }

    return (
      <div className="border rounded-lg p-4 bg-white">
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Name</label>
            <input
              type="text"
              value={editedPAX.name}
              onChange={(e) => updatePAX('name', e.target.value)}
              className="pricing-input w-full text-sm"
              required
              placeholder="Enter category name"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Number of Persons
            </label>
            <input
              type="number"
              value={editedPAX.persons}
              onChange={(e) => {
                const val = parseInt(e.target.value)
                if (!isNaN(val) && val >= 1) {
                  updatePAX('persons', val)
                }
              }}
              className="pricing-input w-full text-sm"
              min="1"
              required
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Selling price difference type
            </label>
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50 w-full mb-2">
              <button
                type="button"
                className={`w-1/2 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  editedPAX.sellingPriceDifferenceType === 'absolute'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() =>
                  updatePAX('sellingPriceDifferenceType', 'absolute')
                }
              >
                Absolute
              </button>
              <button
                type="button"
                className={`w-1/2 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  editedPAX.sellingPriceDifferenceType === 'percent'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() =>
                  updatePAX('sellingPriceDifferenceType', 'percent')
                }
              >
                Percent
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">
              Selling price difference
            </label>
            <div className="relative">
              <input
                type="number"
                value={editedPAX.sellingPriceDifference ?? ''}
                onChange={(e) =>
                  updatePAX(
                    'sellingPriceDifference',
                    parseFloat(e.target.value) || 0,
                  )
                }
                className="pricing-input w-full text-sm pr-8"
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                {editedPAX.sellingPriceDifferenceType === 'absolute'
                  ? '€'
                  : '%'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isDefault"
              checked={editedPAX.isDefault}
              onChange={(e) => updatePAX('isDefault', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isDefault" className="text-sm text-gray-600">
              Set as default PAX category
            </label>
          </div>

          {businessModel.type === 'agency' && (
            <>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Input price difference type
                </label>
                <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50 w-full mb-2">
                  <button
                    type="button"
                    className={`w-1/2 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      editedPAX.inputPriceDifferenceType === 'absolute'
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() =>
                      updatePAX('inputPriceDifferenceType', 'absolute')
                    }
                  >
                    Absolute
                  </button>
                  <button
                    type="button"
                    className={`w-1/2 px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      editedPAX.inputPriceDifferenceType === 'percent'
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    onClick={() =>
                      updatePAX('inputPriceDifferenceType', 'percent')
                    }
                  >
                    Percent
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Input price difference
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={editedPAX.inputPriceDifference ?? ''}
                    onChange={(e) =>
                      updatePAX(
                        'inputPriceDifference',
                        parseFloat(e.target.value) || 0,
                      )
                    }
                    className="pricing-input w-full text-sm pr-8"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                    {editedPAX.inputPriceDifferenceType === 'absolute'
                      ? '€'
                      : '%'}
                  </span>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-between pt-2 relative z-10">
            <div className="flex gap-2">
              <button
                onClick={
                  isNew
                    ? handleCancelNewPAX
                    : () => {
                        console.log('Cancel clicked')
                        setEditingPAXId(null)
                        setEditedPAX(null)
                      }
                }
                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('Save clicked, editedPAX:', editedPAX)
                  handleSavePAX()
                }}
                className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
              >
                Save
              </button>
            </div>
            {!isNew && (
              <button
                onClick={() => {
                  console.log('Delete clicked')
                  handleDeletePAX(editedPAX.id)
                  setEditingPAXId(null)
                  setEditedPAX(null)
                }}
                className="px-3 py-1 text-xs bg-danger-600 text-white rounded hover:bg-danger-700 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderTypeSelection = () => {
    if (!showTypeOptions) {
      return (
        <div>
          <h4 className="font-medium text-gray-900">
            {businessModel.type === 'direct' ? 'Direct' : 'Agency Model'}
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            {businessModel.type === 'direct'
              ? 'You manage and rent out properties directly to guests. Full control over pricing and operations.'
              : 'You act as an agency managing properties for owners. Requires commission and VAT setup.'}
          </p>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 gap-3">
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            businessModel.type === 'direct'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
          }`}
          onClick={() => {
            onBusinessModelChange({
              ...businessModel,
              type: 'direct',
            })
            setShowTypeOptions(false)
          }}
        >
          <h4 className="font-medium text-gray-900">Direct</h4>
          <p className="text-sm text-gray-500 mt-1">
            You manage and rent out properties directly to guests. Full control
            over pricing and operations.
          </p>
        </div>

        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            businessModel.type === 'agency'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
          }`}
          onClick={() => {
            onBusinessModelChange({
              ...businessModel,
              type: 'agency',
            })
            setShowTypeOptions(false)
          }}
        >
          <h4 className="font-medium text-gray-900">Agency Model</h4>
          <p className="text-sm text-gray-500 mt-1">
            You act as an agency managing properties for owners. Requires
            commission and VAT setup.
          </p>
        </div>
      </div>
    )
  }

  const renderWorkModelSelection = () => {
    if (!showWorkModelOptions) {
      const workModelLabels: Record<
        NonNullable<BusinessModel['workModel']>,
        string
      > = {
        own_else: 'In own name and for own account',
        own_else_commission: "In own name and else's account",
        own_else_commission_prepayment: "In else's name and for else's account",
      }
      const currentWorkModel = businessModel.workModel || 'own_else'
      return (
        <div>
          <h4 className="font-medium text-gray-900">
            {workModelLabels[currentWorkModel]}
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            {currentWorkModel === 'own_else'
              ? 'You manage the property and handle all payments directly. Best for direct property management.'
              : currentWorkModel === 'own_else_commission'
              ? 'You manage the property but handle payments through a third party. Requires commission setup.'
              : 'Property is managed by a third party and all payments go through them. Full delegation model.'}
          </p>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 gap-3">
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            businessModel.workModel === 'own_else'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
          }`}
          onClick={() => {
            onBusinessModelChange({
              ...businessModel,
              workModel: 'own_else',
            })
            setShowWorkModelOptions(false)
          }}
        >
          <h4 className="font-medium text-gray-900">
            In own name and for own account
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            You manage the property and handle all payments directly. Best for
            direct property management.
          </p>
        </div>

        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            businessModel.workModel === 'own_else_commission'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
          }`}
          onClick={() => {
            onBusinessModelChange({
              ...businessModel,
              workModel: 'own_else_commission',
            })
            setShowWorkModelOptions(false)
          }}
        >
          <h4 className="font-medium text-gray-900">
            In own name and else's account
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            You manage the property but handle payments through a third party.
            Requires commission setup.
          </p>
        </div>

        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            businessModel.workModel === 'own_else_commission_prepayment'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
          }`}
          onClick={() => {
            onBusinessModelChange({
              ...businessModel,
              workModel: 'own_else_commission_prepayment',
            })
            setShowWorkModelOptions(false)
          }}
        >
          <h4 className="font-medium text-gray-900">
            In else's name and for else's account
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            Property is managed by a third party and all payments go through
            them. Full delegation model.
          </p>
        </div>
      </div>
    )
  }

  const renderPriceModelSelection = () => {
    if (!showPriceModelOptions) {
      const priceModelLabels = {
        absolute_same: 'Absolute, same for all rate plans',
        absolute_different: 'Absolute, different for different rate plans',
        relative_percentage: 'Relative to output (selling) prices',
      }
      return (
        <div>
          <h4 className="font-medium text-gray-900">
            {priceModelLabels[businessModel.model]}
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            {businessModel.model === 'absolute_same'
              ? 'Use the same fixed price across all rate plans. Simplest pricing model.'
              : businessModel.model === 'absolute_different'
              ? 'Set different fixed prices for each rate plan. More flexible pricing control.'
              : 'Calculate input prices as a percentage of selling prices. Requires owner payout percentage.'}
          </p>
        </div>
      )
    }

    return (
      <div className="grid grid-cols-1 gap-3">
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            businessModel.model === 'absolute_same'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
          }`}
          onClick={() => {
            onBusinessModelChange({
              ...businessModel,
              model: 'absolute_same',
            })
            setShowPriceModelOptions(false)
          }}
        >
          <h4 className="font-medium text-gray-900">
            Absolute, same for all rate plans
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            Use the same fixed price across all rate plans. Simplest pricing
            model.
          </p>
        </div>

        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            businessModel.model === 'absolute_different'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
          }`}
          onClick={() => {
            onBusinessModelChange({
              ...businessModel,
              model: 'absolute_different',
            })
            setShowPriceModelOptions(false)
          }}
        >
          <h4 className="font-medium text-gray-900">
            Absolute, different for different rate plans
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            Set different fixed prices for each rate plan. More flexible pricing
            control.
          </p>
        </div>

        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            businessModel.model === 'relative_percentage'
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
          }`}
          onClick={() => {
            onBusinessModelChange({
              ...businessModel,
              model: 'relative_percentage',
            })
            setShowPriceModelOptions(false)
          }}
        >
          <h4 className="font-medium text-gray-900">
            Relative to output (selling) prices
          </h4>
          <p className="text-sm text-gray-500 mt-1">
            Calculate input prices as a percentage of selling prices. Requires
            owner payout percentage.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full bg-white border-r border-gray-200 p-6 overflow-y-auto">
      <div className="flex items-center gap-2 mb-6">
        <Settings className="w-5 h-5 text-primary-600" />
        <h2 className="text-xl font-semibold text-gray-900">
          Pricing Configuration
        </h2>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-2 px-4 text-sm font-medium ${
            activeTab === 'business'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('business')}
        >
          Business Model
        </button>
        <button
          className={`py-2 px-4 text-sm font-medium ${
            activeTab === 'channels'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('channels')}
        >
          Sales Channels
        </button>
        <button
          className={`py-2 px-4 text-sm font-medium ${
            activeTab === 'rules'
              ? 'text-primary-600 border-b-2 border-primary-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('rules')}
        >
          Pricing Rules
        </button>
      </div>

      {activeTab === 'business' ? (
        <>
          {/* Business Model */}
          <div className="mb-8">
            <div className="space-y-6">
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Type
                  </label>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setShowTypeOptions(!showTypeOptions)
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                  >
                    {showTypeOptions ? (
                      <>
                        Close <X className="w-3 h-3" />
                      </>
                    ) : (
                      <>
                        Select <ChevronDown className="w-3 h-3" />
                      </>
                    )}
                  </a>
                </div>
                {renderTypeSelection()}
              </div>

              {businessModel.type === 'agency' && (
                <>
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Work Model
                      </label>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          setShowWorkModelOptions(!showWorkModelOptions)
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                      >
                        {showWorkModelOptions ? (
                          <>
                            Close <X className="w-3 h-3" />
                          </>
                        ) : (
                          <>
                            Select <ChevronDown className="w-3 h-3" />
                          </>
                        )}
                      </a>
                    </div>
                    {renderWorkModelSelection()}
                  </div>

                  {/* VAT rate and Agency commission only for 'own_else_commission' */}
                  {businessModel.workModel === 'own_else_commission' && (
                    <div className="border rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">
                        VAT and Commission Settings
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            VAT rate on advance payment for accommodation*
                          </label>
                          <input
                            type="number"
                            value={businessModel.vatRate || ''}
                            onChange={(e) =>
                              onBusinessModelChange({
                                ...businessModel,
                                vatRate: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="pricing-input w-full"
                            placeholder="0.00"
                            min="0"
                            max="100"
                            title="Enter the VAT rate applicable to your commission for tax reporting"
                          />
                          {businessModel.vatRate === undefined && (
                            <span className="text-xs text-danger-600">
                              Required field
                            </span>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Agency commission on prepayments*
                          </label>
                          <input
                            type="number"
                            value={businessModel.agencyFee || ''}
                            onChange={(e) =>
                              onBusinessModelChange({
                                ...businessModel,
                                agencyFee: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="pricing-input w-full"
                            placeholder="0.00"
                            min="0"
                            max="100"
                            title="Set your commission percentage for handling prepayments"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Input price forming model
                      </label>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          setShowPriceModelOptions(!showPriceModelOptions)
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 transition-colors"
                      >
                        {showPriceModelOptions ? (
                          <>
                            Close <X className="w-3 h-3" />
                          </>
                        ) : (
                          <>
                            Select <ChevronDown className="w-3 h-3" />
                          </>
                        )}
                      </a>
                    </div>
                    {renderPriceModelSelection()}
                  </div>

                  {businessModel.type === 'agency' &&
                    (businessModel.model === 'absolute_same' ||
                      businessModel.model === 'absolute_different') && (
                      <div className="border rounded-lg p-4 mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Agency absolute fee per reservation (€)
                        </label>
                        <input
                          type="number"
                          value={businessModel.agencyFee || ''}
                          onChange={(e) =>
                            onBusinessModelChange({
                              ...businessModel,
                              agencyFee: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="pricing-input w-full"
                          placeholder="e.g. 20"
                          min="0"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This is the fixed amount the agency receives for each
                          reservation. The rest goes to the owner.
                        </p>
                      </div>
                    )}

                  {businessModel.type === 'agency' &&
                    businessModel.model === 'relative_percentage' && (
                      <div className="border rounded-lg p-4 mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Owner payout percent (%)
                        </label>
                        <input
                          type="number"
                          value={businessModel.ownerPayoutPercent || ''}
                          onChange={(e) =>
                            onBusinessModelChange({
                              ...businessModel,
                              ownerPayoutPercent:
                                parseFloat(e.target.value) || 0,
                            })
                          }
                          className="pricing-input w-full"
                          placeholder="e.g. 70"
                          min="0"
                          max="100"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Percentage of the selling price that goes to the
                          property owner. The rest goes to the agency.
                        </p>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        </>
      ) : activeTab === 'channels' ? (
        <>
          {/* OTA Channels */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Sales Channels
            </h3>

            <div className="space-y-3">
              {otaChannels.map((channel) => (
                <div
                  key={channel.id}
                  className="border border-gray-200 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={channel.enabled}
                        onChange={() => handleChannelToggle(channel.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="font-medium text-sm">
                        {channel.name}
                      </span>
                    </div>
                  </div>

                  {channel.enabled && channel.id !== 'direct' && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Margin (%)
                      </label>
                      <input
                        type="number"
                        value={channel.marginPercent}
                        onChange={(e) =>
                          handleChannelMarginChange(channel.id, e.target.value)
                        }
                        className="pricing-input w-full text-sm"
                        placeholder="15"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Pricing Rules Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Active Pricing Rules
              </h3>
              <button
                onClick={handleAddRule}
                className="px-3 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-1"
                disabled={newRule !== null || editingRuleId !== null}
              >
                <Plus className="w-3 h-3" />
                Add Rule
              </button>
            </div>

            <div className="space-y-4">
              {newRule && renderRuleForm(true)}

              {pricingRules.map((rule) => {
                // Determine rule type for label
                let ruleTypeLabel = ''
                let ruleTypeColor = ''
                const nameLower = rule.name.toLowerCase()
                if (
                  nameLower.includes('holiday') ||
                  nameLower.includes('special') ||
                  (rule.dateRangeFilter &&
                    !rule.dayOfWeekFilter &&
                    !rule.monthFilter)
                ) {
                  ruleTypeLabel = 'Special Day'
                  ruleTypeColor = 'bg-pink-100 text-pink-700 border-pink-300'
                } else if (
                  rule.monthFilter ||
                  (rule.dateRangeFilter && !ruleTypeLabel)
                ) {
                  ruleTypeLabel = 'Season'
                  ruleTypeColor = 'bg-blue-100 text-blue-700 border-blue-300'
                } else if (rule.dayOfWeekFilter) {
                  ruleTypeLabel = 'Repeating Day'
                  ruleTypeColor = 'bg-green-100 text-green-700 border-green-300'
                }
                return (
                  <div key={rule.id}>
                    {editingRuleId === rule.id ? (
                      renderRuleForm(false)
                    ) : (
                      <div
                        className="border border-gray-200 rounded-lg px-4 py-3 mb-3 bg-white"
                        onClick={() => handleEditRule(rule.id)}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-sm">
                            {rule.name}
                          </span>
                          <span
                            className={`text-sm font-medium ${
                              rule.priceModifier === 0
                                ? 'text-blue-600'
                                : rule.priceModifier > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            {rule.priceModifier > 0 ? '+' : ''}
                            {rule.priceModifier}%
                          </span>
                        </div>
                        {/* Dates/Months/Days row with tag */}
                        {(rule.dayOfWeekFilter ||
                          rule.monthFilter ||
                          rule.dateRangeFilter ||
                          ruleTypeLabel) && (
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            {rule.dayOfWeekFilter &&
                              rule.dayOfWeekFilter.length > 0 && (
                                <span>
                                  {rule.dayOfWeekFilter
                                    .map(
                                      (day) =>
                                        [
                                          'Sun',
                                          'Mon',
                                          'Tue',
                                          'Wed',
                                          'Thu',
                                          'Fri',
                                          'Sat',
                                        ][day],
                                    )
                                    .join(', ')}
                                </span>
                              )}
                            {rule.monthFilter &&
                              rule.monthFilter.length > 0 && (
                                <span>
                                  {rule.monthFilter
                                    .map(
                                      (month) =>
                                        [
                                          'Jan',
                                          'Feb',
                                          'Mar',
                                          'Apr',
                                          'May',
                                          'Jun',
                                          'Jul',
                                          'Aug',
                                          'Sep',
                                          'Oct',
                                          'Nov',
                                          'Dec',
                                        ][month - 1],
                                    )
                                    .join(', ')}
                                </span>
                              )}
                            {rule.dateRangeFilter && (
                              <span>
                                {format(
                                  rule.dateRangeFilter.start,
                                  'MMM d, yyyy',
                                )}{' '}
                                -{' '}
                                {format(
                                  rule.dateRangeFilter.end,
                                  'MMM d, yyyy',
                                )}
                              </span>
                            )}
                            <span className="flex-1"></span>
                            {ruleTypeLabel && (
                              <span className="text-gray-400 italic text-xs ml-auto">
                                {ruleTypeLabel}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* LOS Categories Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Length of Stay
              </h3>
              <button
                onClick={handleAddLOS}
                className="px-3 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-1"
                disabled={newLOS !== null || editingLOSId !== null}
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>

            <div className="space-y-3">
              {newLOS && renderLOSForm(true)}

              {losCategories.map((los) => (
                <div key={los.id}>
                  {editingLOSId === los.id ? (
                    renderLOSForm(false)
                  ) : (
                    <div
                      className="border border-gray-200 rounded-lg px-4 py-3 mb-3 bg-white"
                      onClick={() => handleEditLOS(los.id)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-900">
                          {los.name}
                        </span>
                        <span
                          className={`text-sm font-medium ${
                            los.sellingPriceDifferenceType === 'percent'
                              ? (los.sellingPriceDifference ?? 0) === 0
                                ? 'text-blue-600'
                                : (los.sellingPriceDifference ?? 0) > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                              : (los.sellingPriceDifference ?? 0) === 0
                              ? 'text-blue-600'
                              : (los.sellingPriceDifference ?? 0) > 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          {(los.sellingPriceDifference ?? 0) > 0 ? '+' : ''}
                          {los.sellingPriceDifference ?? 0}
                          {los.sellingPriceDifferenceType === 'percent'
                            ? '%'
                            : '€'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="flex justify-center">
                          <Clock className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        </span>
                        <span className="text-sm text-gray-500 font-normal">
                          {los.minNights} - {los.maxNights || '∞'} nights
                        </span>
                        {los.isDefault && (
                          <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* PAX Categories Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Guest Count
              </h3>
              <button
                onClick={handleAddPAX}
                className="px-3 py-1 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-1"
                disabled={newPAX !== null || editingPAXId !== null}
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>

            <div className="space-y-3">
              {newPAX && renderPAXForm(true)}

              {paxCategories.map((pax) => (
                <div key={pax.id}>
                  {editingPAXId === pax.id ? (
                    renderPAXForm(false)
                  ) : (
                    <div
                      className="border border-gray-200 rounded-lg px-4 py-3 mb-3 bg-white"
                      onClick={() => handleEditPAX(pax.id)}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-900">
                          {pax.name}
                        </span>
                        <span
                          className={`text-sm font-medium ${
                            pax.sellingPriceDifferenceType === 'percent'
                              ? (pax.sellingPriceDifference ?? 0) === 0
                                ? 'text-blue-600'
                                : (pax.sellingPriceDifference ?? 0) > 0
                                ? 'text-green-600'
                                : 'text-red-600'
                              : (pax.sellingPriceDifference ?? 0) === 0
                              ? 'text-blue-600'
                              : (pax.sellingPriceDifference ?? 0) > 0
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}
                        >
                          {(pax.sellingPriceDifference ?? 0) > 0 ? '+' : ''}
                          {pax.sellingPriceDifference ?? 0}
                          {pax.sellingPriceDifferenceType === 'percent'
                            ? '%'
                            : '€'}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mt-1">
                        <span className="flex justify-center">
                          <Users className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        </span>
                        <span className="text-sm text-gray-500 font-normal">
                          {pax.persons} person{pax.persons !== 1 ? 's' : ''}
                        </span>
                        {pax.isDefault && (
                          <span className="ml-auto px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default PriceConfigPanel
